import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Snackbar, Alert, Chip,
  MenuItem, Select, FormControl, InputLabel, InputAdornment, Avatar,
  Card, CardContent, Stack, Divider, FormControlLabel,
  LinearProgress, Tooltip, Radio, RadioGroup, FormLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LockResetIcon from "@mui/icons-material/LockReset";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SendIcon from "@mui/icons-material/Send";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  fetchUsers, fetchRoles, createUser, updateUser, deleteUser,
  sendUserResetLink, sendUserResetSms, setUserPassword, sendUserCredentials, sendUserCredentialsSms,
  checkUserDuplicatePhone,
  IUserManaged, IRole,
} from "../../../Api/rbacApi";
import { Loader } from "../../../Components/Loader";
import { uploadImage } from "../../../Components/UploadFile/upload";
import AvatarPhotoEditor from "../../../Components/AvatarPhotoEditor";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";

const API_URL = process.env.REACT_APP_API_URL || "";

/* Build the public URL for a stored image path. Matches the convention
   used by banquet services: API_URL ends in /api, so /../ drops the
   /api segment and reaches Laravel's public storage symlink. */
const buildImageUrl = (path: string | null | undefined): string =>
  path ? `${API_URL}/../${path}` : "";

/* ── Constants ──────────────────────────────────────────────────────── */

/* Premium field styling — applied to every input in the Create/Edit User
   dialog so the form reads as a cohesive enterprise SaaS surface.
   Slightly taller inputs (44px), softer hairline border, navy focus ring,
   and a muted slate label palette. */
const PREMIUM_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    transition: "all 0.18s",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(26,51,83,0.08)",
      "& fieldset": { borderColor: "#1a3353", borderWidth: "1px" },
    },
    "&.Mui-error.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(220,38,38,0.08)",
    },
    "&.Mui-error fieldset": { borderColor: "#dc2626" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, py: "12.5px" },
  "& .MuiInputLabel-root": {
    fontSize: 13, fontWeight: 500, color: "#64748b",
    "&.Mui-focused": { color: "#1a3353" },
    "&.Mui-error": { color: "#dc2626" },
  },
} as const;

/* Helper-text styling — muted slate by default, soft red on error. */
const PREMIUM_HELPER_SX = (variant: "muted" | "error") => ({
  fontSize: 11,
  fontWeight: variant === "error" ? 600 : 500,
  color: variant === "error" ? "#dc2626" : "#94a3b8",
  ml: 0.2,
  mt: 0.5,
});

const ROLE_COLORS: Record<string, string> = {
  Admin: "#1a3353",
  "Sales Manager": "#2e7d32",
  Receptionist: "#ed6c02",
  Accountant: "#7b1fa2",
  Staff: "#546e7a",
};

/* ── Password Strength ──────────────────────────────────────────────── */

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: "", color: "#e0e0e0" };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score, label: "Weak",   color: "#f44336" };
  if (score <= 3) return { score, label: "Fair",   color: "#ff9800" };
  if (score <= 4) return { score, label: "Good",   color: "#2196f3" };
  return              { score, label: "Strong", color: "#4caf50" };
}

/* ── Auto-generate Password (client-side preview) ───────────────────── */

function generatePassword(): string {
  const upper   = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower   = "abcdefghijklmnopqrstuvwxyz";
  const digits  = "0123456789";
  const special = "!@#$%&*_+-";
  const all     = upper + lower + digits + special;
  let pw = "";
  pw += upper[Math.floor(Math.random() * upper.length)];
  pw += lower[Math.floor(Math.random() * lower.length)];
  pw += digits[Math.floor(Math.random() * digits.length)];
  pw += special[Math.floor(Math.random() * special.length)];
  for (let i = 4; i < 12; i++) pw += all[Math.floor(Math.random() * all.length)];
  return pw.split("").sort(() => Math.random() - 0.5).join("");
}

/* ── Phone Validation ───────────────────────────────────────────────── */

function isValidPhone(phone: string): boolean {
  return /^\+?[\d\s\-().]{7,}$/.test(phone) && phone.replace(/\D/g, "").length >= 7;
}

/**
 * Mirror of backend `PhoneNumber::normalize()` — strips every non-digit
 * and drops a leading "1" country code so "+1 (647) 555-1234" and
 * "6475551234" collapse to the same canonical key. Used for the live
 * in-memory duplicate check so the comparison matches whatever the
 * backend index will see.
 */
function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  return digits;
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */

const UserList: React.FC = () => {
  const [users, setUsers]   = useState<IUserManaged[]>([]);
  const [roles, setRoles]   = useState<IRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  /* ── Create / Edit dialog ── */
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editUser, setEditUser]         = useState<IUserManaged | null>(null);
  const [firstName, setFirstName]       = useState("");
  const [lastName, setLastName]         = useState("");
  const [email, setEmail]               = useState("");
  const [phone, setPhone]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [roleId, setRoleId]             = useState<number | "">("");
  const [profileImage, setProfileImage] = useState<string>("");
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError]     = useState("");
  const fileInputRef                    = React.useRef<HTMLInputElement>(null);
  // Drives the AvatarPhotoEditor modal — opened by the avatar tile,
  // the "Upload" button, or the "Replace" button. The editor handles
  // both webcam capture and file picking, returning a cropped Blob.
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [emailDupError, setEmailDupError] = useState("");
  // Scoped phone duplicate state — checked only against the users table.
  // A customer or a lead with the same phone is irrelevant here.
  const [phoneDupError, setPhoneDupError] = useState("");
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);

  /* ── Post-creation: send credentials dialog ── */
  const [createdUserCredentials, setCreatedUserCredentials] =
    useState<{ user: IUserManaged; password: string } | null>(null);
  const [sendingCreds, setSendingCreds] = useState(false);
  const [sendingCredsSms, setSendingCredsSms] = useState(false);
  const [credsCopied, setCredsCopied]   = useState(false);

  /* ── Delete dialog ── */
  const [deleteConfirm, setDeleteConfirm] = useState<IUserManaged | null>(null);

  /* ── Reset Password dialog ── */
  const [resetUser, setResetUser]         = useState<IUserManaged | null>(null);
  const [resetMode, setResetMode]         = useState<"link" | "sms" | "manual">("link");
  const [newPassword, setNewPassword]     = useState("");
  const [showNewPw, setShowNewPw]         = useState(false);
  const [resetSaving, setResetSaving]     = useState(false);
  const [manualResetUrl, setManualResetUrl] = useState<string | null>(null);
  const [urlCopied, setUrlCopied]           = useState(false);

  /* ── Snackbar ── */
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" as "success" | "error" });
  const toast = (msg: string, severity: "success" | "error" = "success") =>
    setSnack({ open: true, msg, severity });

  /* ── Load ── */
  const loadData = async () => {
    setLoading(true);
    const [u, r] = await Promise.all([fetchUsers(), fetchRoles()]);
    setUsers(u);
    setRoles(r);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);

  const filtered = users.filter((u) =>
    (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.role_name || "").toLowerCase().includes(search.toLowerCase())
  );

  /* ── Open Create ── */
  const openCreate = () => {
    setEditUser(null);
    setFirstName(""); setLastName(""); setEmail(""); setPhone("");
    setPassword(""); setRoleId(""); setShowPassword(false);
    setProfileImage(""); setImageError(""); setImageUploading(false);
    setEmailDupError("");
    setPhoneDupError("");
    setDialogOpen(true);
  };

  /* ── Open Edit ── */
  const openEdit = (user: IUserManaged) => {
    setEditUser(user);
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setPassword("");
    setRoleId(user.role_id || "");
    setProfileImage(user.profile_image || "");
    setImageError("");
    setImageUploading(false);
    setShowPassword(false);
    setEmailDupError("");
    setPhoneDupError("");
    setDialogOpen(true);
  };

  /* ── Image upload — uploads via the shared /utility/upload endpoint
       and stores the returned RELATIVE path (e.g. "images/abc.png").
       The full URL is built at render time so the value remains
       portable across environments. ── */
  const handleImageFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setImageError("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setImageError("Image must be under 2MB");
      return;
    }
    setImageError("");
    setImageUploading(true);
    try {
      const path = await uploadImage(file);
      if (path) setProfileImage(path);
    } catch {
      setImageError("Upload failed. Try again.");
    } finally {
      setImageUploading(false);
    }
  };

  /* ── Receive the cropped Blob from AvatarPhotoEditor ──
     The editor emits a 512×512 JPEG blob. Wrap it as a File so the
     existing /utility/upload endpoint sees the expected shape, and
     reuse the same upload + state-update flow as the file picker. */
  const handleCroppedPhoto = async (blob: Blob) => {
    setImageError("");
    setImageUploading(true);
    try {
      const file = new File([blob], `avatar-${Date.now()}.jpg`, { type: "image/jpeg" });
      const path = await uploadImage(file);
      if (path) setProfileImage(path);
    } catch {
      setImageError("Upload failed. Try again.");
    } finally {
      setImageUploading(false);
    }
  };

  /* ── Live phone duplicate check (scoped to USERS table only) ──
     Mirrors the email duplicate UX exactly:
       1. Instant in-memory check against the already-loaded `users`
          list so the operator sees the error the moment they leave
          the field — no network wait.
       2. Backend confirmation as a race-safety net (the in-memory
          list might be stale if someone else just created a user).

     The instant in-memory check uses `normalizePhone()` so formatting
     differences ("(647) 809-1659" vs "6478091659") don't slip past. */
  const checkPhoneDuplicateInstant = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || !isValidPhone(trimmed)) return;          // bad format → format error wins
    const normalized = normalizePhone(trimmed);
    if (!normalized) return;
    const dupe = users.find(
      (u) => normalizePhone(u.phone) === normalized && u.id !== editUser?.id
    );
    if (dupe) {
      const name = (dupe.first_name || dupe.last_name)
        ? `${dupe.first_name ?? ""} ${dupe.last_name ?? ""}`.trim()
        : (dupe.email || `User #${dupe.id}`);
      setPhoneDupError(`This phone number is already used by user "${name}".`);
    } else {
      setPhoneDupError("");
    }
  };

  const verifyPhoneUniqueAmongUsers = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || !isValidPhone(trimmed)) {
      setPhoneDupError("");
      return;
    }
    // Step 1 — instant in-memory check (matches email UX)
    checkPhoneDuplicateInstant(trimmed);

    // Step 2 — backend confirmation (catches races and stale lists)
    try {
      setPhoneCheckLoading(true);
      const res = await checkUserDuplicatePhone(trimmed, editUser?.id);
      if (res.duplicate && res.user) {
        setPhoneDupError(`This phone number is already used by user "${res.user.name}".`);
      } else {
        // Only clear if the in-memory check also said clean — otherwise
        // trust the in-memory result (the backend can't possibly know
        // less than what we already know from the loaded list).
        setPhoneDupError((prev) => (prev ? "" : ""));
      }
    } catch {
      // Silent — the in-memory check already gave the operator a clear
      // verdict; backend call is just a safety net.
    } finally {
      setPhoneCheckLoading(false);
    }
  };

  /* ── Validation ──
     Create: every field including phone is required (mirrors the
     backend store() validator which marks phone as `required`).
     Edit:   phone is allowed empty (mirrors the backend update()
     validator which marks phone as `nullable`). This matters because
     existing users whose phone was nulled during the uniqueness
     migration would otherwise have their Update button stuck disabled.
     Whenever a phone IS present, it must still parse as valid. */
  const isFormValid = () => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    const phoneTrimmed = phone.trim();
    if (!editUser) {
      // CREATE mode — phone is required
      if (!phoneTrimmed || !isValidPhone(phoneTrimmed)) return false;
    } else {
      // EDIT mode — phone is optional, but if entered must be valid
      if (phoneTrimmed && !isValidPhone(phoneTrimmed)) return false;
    }
    if (!roleId) return false;
    if (password && password.length < 8) return false;
    return true;
  };

  /* ── Save (Create / Edit) ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      if (editUser) {
        const data: any = {
          first_name: firstName || null,
          last_name:  lastName  || null,
          email:      email     || undefined,
          phone:      phone     || undefined,
          profile_image: profileImage,
          role_id:    roleId    || undefined,
        };
        if (password) data.password = password;
        await updateUser(editUser.id, data);
        toast("User updated successfully");
      } else {
        const result = await createUser({
          first_name: firstName,
          last_name:  lastName,
          email,
          phone,
          profile_image: profileImage || null,
          password:   password || undefined,
          role_id:    roleId as number,
        });
        setDialogOpen(false);
        setSearch(""); // clear filter so new user is visible
        await loadData();
        setCreatedUserCredentials({ user: result.data, password: result.plain_password });
        return; // skip the toast below — credentials dialog replaces it
      }
      setDialogOpen(false);
      loadData();
    } catch (e: any) {
      toast(e.message || "Error saving user", "error");
    }
    setSaving(false);
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteUser(deleteConfirm.id);
      toast("User deleted successfully");
      setDeleteConfirm(null);
      loadData();
    } catch (e: any) {
      toast(e.message || "Error", "error");
    }
  };

  /* ── Reset Password ── */
  const openResetDialog = (user: IUserManaged) => {
    setResetUser(user);
    setResetMode("link");
    setNewPassword("");
    setShowNewPw(false);
    setResetSaving(false);
  };

  const handleReset = async () => {
    if (!resetUser) return;
    setResetSaving(true);
    try {
      if (resetMode === "link") {
        const result = await sendUserResetLink(resetUser.id);
        if (!result.email_sent && result.reset_url) {
          // Mail not configured — show URL so admin can share it manually
          setManualResetUrl(result.reset_url);
          setResetUser(null);
        } else {
          toast(result.message);
          setResetUser(null);
        }
      } else if (resetMode === "sms") {
        const result = await sendUserResetSms(resetUser.id);
        if (!result.sms_sent && result.reset_url) {
          // SMS not configured / failed — show URL so admin can share it manually
          setManualResetUrl(result.reset_url);
          setResetUser(null);
        } else {
          toast(result.message);
          setResetUser(null);
        }
      } else {
        if (!newPassword || newPassword.length < 8) {
          toast("Password must be at least 8 characters", "error");
          setResetSaving(false);
          return;
        }
        const msg = await setUserPassword(resetUser.id, newPassword);
        toast(msg);
        setResetUser(null);
      }
    } catch (e: any) {
      toast(e.message || "Error", "error");
    }
    setResetSaving(false);
  };

  const handleCopyResetUrl = () => {
    if (!manualResetUrl) return;
    navigator.clipboard.writeText(manualResetUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2500);
    });
  };

  const handleSendCredentials = async () => {
    if (!createdUserCredentials) return;
    setSendingCreds(true);
    try {
      await sendUserCredentials(createdUserCredentials.user.id, createdUserCredentials.password);
      toast("Login credentials sent via email.");
      setCreatedUserCredentials(null);
    } catch (e: any) {
      toast(e.message || "Failed to send credentials", "error");
    }
    setSendingCreds(false);
  };

  const handleSendCredentialsSms = async () => {
    if (!createdUserCredentials) return;
    setSendingCredsSms(true);
    try {
      const result = await sendUserCredentialsSms(createdUserCredentials.user.id, createdUserCredentials.password);
      toast(result.message, result.sms_sent ? "success" : "error");
      if (result.sms_sent) setCreatedUserCredentials(null);
    } catch (e: any) {
      toast(e.message || "Failed to send credentials via SMS", "error");
    }
    setSendingCredsSms(false);
  };

  const handleCopyPassword = () => {
    if (!createdUserCredentials) return;
    navigator.clipboard.writeText(createdUserCredentials.password).then(() => {
      setCredsCopied(true);
      setTimeout(() => setCredsCopied(false), 2500);
    });
  };

  /* ── Derived ── */
  const pwStrength   = getPasswordStrength(password);
  const newPwStrength = getPasswordStrength(newPassword);

  if (loading) return <Loader height="60vh" />;

  /* ═══════════════════════════════════ RENDER ══════════════════════════ */
  return (
    <Box p={3}>

      {/* ── Header ── */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, background: "linear-gradient(135deg, #1a3353 0%, #2d5f8a 100%)", color: "#fff" }}>
        <CardContent sx={{ py: 3, px: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" gap={2}>
              <PeopleOutlineIcon sx={{ fontSize: 36, opacity: 0.9 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>User Management</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>{users.length} users registered</Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={openCreate}
              sx={{ bgcolor: "#D4AF37", color: "#1a3353", fontWeight: 700, borderRadius: 2, px: 3, "&:hover": { bgcolor: "#b89540" } }}
            >
              Add User
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Search ──
          The Reset Password modal mounts a <input type="password"> when
          the user picks "Set password manually". Chrome / Edge see that
          mount and try to AUTOFILL the nearest visible text input on
          the page as the "username" — landing on this search box. The
          fixes below combine three signals so the browser never targets
          this input:
            • type="search"            — semantic: this is a query, not a username
            • autoComplete="off"       — standard opt-out
            • name="user-search-field" — unique name (not "email"/"username"/etc.)
            • data-lpignore / 1p-ignore — vendor password-manager opt-outs
          The modal itself ALSO has its own decoy inputs + new-password
          autocomplete attribute to soak the autofill at the source. */}
      <TextField
        placeholder="Search users..."
        size="small"
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        inputProps={{
          autoComplete: "off",
          name: "user-search-field",
          "data-lpignore": "true",
          "data-1p-ignore": "true",
        }}
        sx={{ mb: 2, width: 320, bgcolor: "#fff", borderRadius: 2 }}
      />

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8e8e8" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Role</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: "#1a3353", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((user, i) => {
              const initials  = (user.name || user.email || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
              const roleColor = ROLE_COLORS[user.role_name] || "#546e7a";
              return (
                <TableRow key={user.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ color: "#999", fontWeight: 600 }}>{i + 1}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar
                        src={buildImageUrl(user.profile_image)}
                        sx={{ bgcolor: roleColor, width: 36, height: 36, fontSize: 14, fontWeight: 700 }}
                      >
                        {initials}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{user.name || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          Login: {user.email || user.username}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.email || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{user.phone || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                      label={user.role_name || "No Role"}
                      size="small"
                      sx={{
                        bgcolor: `${roleColor}14`, color: roleColor, fontWeight: 600,
                        border: `1px solid ${roleColor}30`,
                        "& .MuiChip-icon": { color: roleColor },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit user">
                      <IconButton size="small" onClick={() => openEdit(user)} sx={{ color: "#1a3353" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset password">
                      <IconButton size="small" onClick={() => openResetDialog(user)} sx={{ color: "#7b1fa2" }}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {user.id !== 1 && (
                      <Tooltip title="Delete user">
                        <IconButton size="small" onClick={() => setDeleteConfirm(user)} sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#999" }}>No users found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ════════════════════════════════════
          CREATE / EDIT DIALOG
      ════════════════════════════════════ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        fullWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(13,27,42,0.55)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            width: 560,
            maxWidth: "92vw",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(13,27,42,0.28), 0 8px 24px rgba(13,27,42,0.18)",
          },
        }}
      >
        {/* ── Premium navy header — executive management strip ───── */}
        <Box sx={{
          position: "relative",
          background: `
            radial-gradient(ellipse at 95% 0%, rgba(201,168,76,0.12) 0%, transparent 55%),
            linear-gradient(135deg, #1E4D8F 0%, #142b46 65%, #1c3a5f 100%)
          `,
          color: "#fff",
          px: 3.5, py: 2.8,
          borderBottom: "1px solid rgba(201,168,76,0.18)",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0, right: 0, bottom: 0, width: "40%",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "14px 14px",
            maskImage: "linear-gradient(to left, rgba(0,0,0,0.7), transparent)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.7), transparent)",
            pointerEvents: "none",
          },
        }}>
          <Stack direction="row" alignItems="center" gap={1.6} sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px",
              bgcolor: "rgba(201,168,76,0.16)", color: "#D4AF37",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid rgba(201,168,76,0.30)",
            }}>
              <PersonAddIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.3, color: "#D4AF37", textTransform: "uppercase", opacity: 0.9 }}>
                User Management
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.3px", mt: 0.2 }}>
                {editUser ? "Edit User Account" : "Create New User"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.6)", mt: 0.3 }}>
                {editUser
                  ? "Update profile, role, or reset credentials."
                  : "Grant access to a teammate with role-based permissions."}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: "28px 32px 24px 32px !important", bgcolor: "#fafbfc" }}>
          <Stack gap={2.6}>

            {/* ── Section · Personal ────────────────────────────── */}
            <Box>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "#94a3b8", textTransform: "uppercase", mb: 1.4 }}>
                Personal Details
              </Typography>
              {/* Stable, semantic autocomplete attributes per field so the
                  browser / password manager maps each input correctly.
                  Each field has a unique `name` + `id` and the proper
                  HTML5 autocomplete token — no cross-contamination. */}
              <Stack gap={1.6}>
                {/* ── Profile photo uploader ──
                    Optional — if left blank the user's initials are used
                    as the avatar in the user table. Uses the shared
                    /utility/upload endpoint and stores only the returned
                    relative path so the value remains portable. */}
                <Stack direction="row" alignItems="center" gap={2}>
                  <Box
                    onClick={() => setPhotoEditorOpen(true)}
                    sx={{
                      position: "relative",
                      width: 72, height: 72, borderRadius: "50%",
                      bgcolor: "#f1f5f9",
                      border: "2px dashed #cbd5e1",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", cursor: "pointer", flexShrink: 0,
                      transition: "all 0.18s",
                      "&:hover": { borderColor: "#1a3353", bgcolor: "#eef2f7" },
                      "&:hover .upload-overlay": { opacity: 1 },
                    }}
                  >
                    {profileImage ? (
                      <>
                        <Box
                          component="img"
                          src={buildImageUrl(profileImage)}
                          alt="Profile preview"
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <Box
                          className="upload-overlay"
                          sx={{
                            position: "absolute", inset: 0,
                            bgcolor: "rgba(13,27,42,0.55)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            opacity: 0, transition: "opacity 0.18s",
                          }}
                        >
                          {imageUploading
                            ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                            : <AddPhotoAlternateOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />}
                        </Box>
                      </>
                    ) : imageUploading ? (
                      <CircularProgress size={22} sx={{ color: "#1a3353" }} />
                    ) : (
                      <CloudUploadOutlinedIcon sx={{ fontSize: 26, color: "#94a3b8" }} />
                    )}
                  </Box>
                  <Box flex={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a3353", mb: 0.3 }}>
                      Profile Photo
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.45 }}>
                      {profileImage
                        ? "Click the avatar to replace, or remove to use initials instead."
                        : "Optional. If left blank, the user's initials will be shown."}
                    </Typography>
                    <Stack direction="row" gap={0.8} mt={0.8}>
                      <Button
                        size="small"
                        type="button"
                        onClick={() => setPhotoEditorOpen(true)}
                        disabled={imageUploading}
                        startIcon={<AddPhotoAlternateOutlinedIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          textTransform: "none", fontWeight: 600, fontSize: 11.5,
                          color: "#1a3353", borderRadius: "7px", py: 0.3, px: 1.2,
                          bgcolor: "transparent",
                          "&:hover": { bgcolor: "rgba(26,51,83,0.06)" },
                        }}
                      >
                        {profileImage ? "Replace" : "Upload"}
                      </Button>
                      {profileImage && (
                        <Button
                          size="small"
                          type="button"
                          onClick={() => setProfileImage("")}
                          startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: "none", fontWeight: 600, fontSize: 11.5,
                            color: "#dc2626", borderRadius: "7px", py: 0.3, px: 1.2,
                            bgcolor: "transparent",
                            "&:hover": { bgcolor: "rgba(220,38,38,0.06)" },
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                    {imageError && (
                      <Typography sx={{ fontSize: 11, color: "#dc2626", mt: 0.5, fontWeight: 600 }}>
                        {imageError}
                      </Typography>
                    )}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleImageFile(f);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  />
                </Stack>

                {/* Name row */}
                <Stack direction="row" gap={1.4}>
                  <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth required
                    type="text"
                    id="create-user-given-name"
                    name="given-name"
                    inputProps={{ autoComplete: "given-name", id: "create-user-given-name", name: "given-name" }}
                    error={!!firstName && !firstName.trim()}
                    sx={PREMIUM_FIELD_SX}
                  />
                  <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth required
                    type="text"
                    id="create-user-family-name"
                    name="family-name"
                    inputProps={{ autoComplete: "family-name", id: "create-user-family-name", name: "family-name" }}
                    error={!!lastName && !lastName.trim()}
                    sx={PREMIUM_FIELD_SX}
                  />
                </Stack>

                {/* Email — type=email + autocomplete=email so browsers
                    only ever offer email suggestions here. */}
                <TextField
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailDupError(""); }}
                  id="create-user-email"
                  name="email"
                  inputProps={{
                    autoComplete: "email",
                    inputMode: "email",
                    id: "create-user-email",
                    name: "email",
                  }}
                  onBlur={() => {
                    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                    const duplicate = users.find(
                      (u) => u.email?.toLowerCase() === email.toLowerCase() &&
                             u.id !== editUser?.id
                    );
                    setEmailDupError(duplicate ? "This email is already taken by another user." : "");
                  }}
                  fullWidth required
                  error={!!(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) || !!emailDupError}
                  helperText={
                    emailDupError
                      ? emailDupError
                      : email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                        ? "Enter a valid email address"
                        : "Used as the login username"
                  }
                  FormHelperTextProps={{ sx: PREMIUM_HELPER_SX(emailDupError ? "error" : "muted") }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon sx={{ fontSize: 17, color: emailDupError ? "#dc2626" : "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={PREMIUM_FIELD_SX}
                />

                {/* Phone — type=tel + autocomplete=tel + inputMode=tel so
                    the browser shows a phone keypad on mobile and never
                    offers email or password suggestions. Live duplicate
                    check on blur (scoped to USERS table only). */}
                <TextField
                  label="Phone Number"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPhone(v);
                    // Clear any prior error so editing feels responsive,
                    // then re-check against the in-memory list instantly
                    // (no debounce — same pattern as email validation).
                    setPhoneDupError("");
                    checkPhoneDuplicateInstant(v);
                  }}
                  onBlur={() => verifyPhoneUniqueAmongUsers(phone)}
                  id="create-user-phone"
                  name="tel"
                  inputProps={{
                    autoComplete: "tel",
                    inputMode: "tel",
                    id: "create-user-phone",
                    name: "tel",
                  }}
                  fullWidth required
                  error={!!(phone && !isValidPhone(phone)) || !!phoneDupError}
                  helperText={
                    phoneDupError
                      ? phoneDupError
                      : phoneCheckLoading
                        ? "Checking…"
                        : phone && !isValidPhone(phone)
                          ? "Enter a valid phone number (min 7 digits)"
                          : " "
                  }
                  FormHelperTextProps={{
                    sx: PREMIUM_HELPER_SX(
                      phoneDupError || (phone && !isValidPhone(phone)) ? "error" : "muted"
                    ),
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon sx={{
                          fontSize: 17,
                          color: phoneDupError || (phone && !isValidPhone(phone)) ? "#dc2626" : "#94a3b8",
                        }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={PREMIUM_FIELD_SX}
                />
              </Stack>
            </Box>

            {/* ── Section · Credentials ─────────────────────────── */}
            <Box>
              <Stack direction="row" alignItems="center" mb={1.4}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "#94a3b8", textTransform: "uppercase" }}>
                  Credentials
                </Typography>
                <Box flex={1} />
                {!editUser && (
                  <Button
                    size="small"
                    type="button"
                    startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      // Defensive: stop event bubbling so no enclosing
                      // form's submit handler runs, and only the password
                      // state setter is invoked — no other field touched.
                      e.preventDefault();
                      e.stopPropagation();
                      setPassword(generatePassword());
                      setShowPassword(true);
                    }}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontSize: 11.5,
                      color: "#1a3353", borderRadius: "7px", py: 0.3, px: 1,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "rgba(26,51,83,0.06)" },
                    }}
                  >
                    Generate Password
                  </Button>
                )}
              </Stack>

              <TextField
                label={editUser ? "New Password" : "Password"}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                id="create-user-new-password"
                name="new-password"
                placeholder={editUser ? "Enter new password…" : "Leave blank to auto-generate"}
                inputProps={{
                  // `new-password` tells password managers this is for a
                  // freshly-created credential — never autofill with a
                  // saved password, never offer to save it as the
                  // current user's login. `data-1p-ignore` and
                  // `data-lpignore` ask 1Password / LastPass to skip
                  // this field entirely while we toggle visibility.
                  autoComplete: "new-password",
                  id: "create-user-new-password",
                  name: "new-password",
                  spellCheck: false,
                  "data-1p-ignore": "true",
                  "data-lpignore": "true",
                }}
                helperText={
                  editUser
                    ? "Leave blank to keep the current password."
                    : "Leave blank and the system will auto-generate a secure password."
                }
                FormHelperTextProps={{ sx: PREMIUM_HELPER_SX("muted") }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end" size="small"
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        sx={{ color: "#94a3b8", "&:hover": { color: "#1a3353", bgcolor: "transparent" } }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={PREMIUM_FIELD_SX}
              />

              {password && (
                <Box mt={1.2}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.6}>
                    <Stack direction="row" alignItems="center" gap={0.6}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: pwStrength.color }} />
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: pwStrength.color, letterSpacing: 0.3, textTransform: "uppercase" }}>
                        {pwStrength.label}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
                      {password.length} characters
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(pwStrength.score / 5) * 100}
                    sx={{
                      height: 4, borderRadius: 4,
                      bgcolor: "#eef0f4",
                      "& .MuiLinearProgress-bar": { bgcolor: pwStrength.color, borderRadius: 4, transition: "all 0.25s" },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* ── Section · Role ────────────────────────────────── */}
            <Box>
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "#94a3b8", textTransform: "uppercase", mb: 1.4 }}>
                Access & Permissions
              </Typography>
              <FormControl fullWidth required sx={PREMIUM_FIELD_SX}>
                <InputLabel>Role</InputLabel>
                <Select value={roleId} label="Role" onChange={(e) => setRoleId(e.target.value as number)}>
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: "6px",
                          bgcolor: `${ROLE_COLORS[r.name] || "#546e7a"}15`,
                          color: ROLE_COLORS[r.name] || "#546e7a",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />
                        </Box>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{r.name}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

          </Stack>
        </DialogContent>

        {/* ── Premium footer ─────────────────────────────────────── */}
        <DialogActions sx={{
          px: 3.5, py: 2,
          bgcolor: "#fafbfc",
          borderTop: "1px solid #eef0f4",
          gap: 0.8,
        }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              color: "#64748b", px: 2.4, py: 0.9, borderRadius: "10px",
              "&:hover": { color: "#1a3353", bgcolor: "rgba(26,51,83,0.04)" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" disableElevation
            onClick={handleSave}
            disabled={saving || imageUploading || !isFormValid() || !!emailDupError || !!phoneDupError || phoneCheckLoading}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 13,
              bgcolor: "#1E4D8F", color: "#fff",
              borderRadius: "10px", px: 3, py: 1,
              letterSpacing: 0.2,
              boxShadow: "0 4px 12px rgba(13,27,42,0.28)",
              "&:hover": { bgcolor: "#142b46", boxShadow: "0 6px 18px rgba(13,27,42,0.34)" },
              "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff", boxShadow: "none" },
            }}
          >
            {saving ? (editUser ? "Saving…" : "Creating…") : (editUser ? "Update User" : "Create User")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════
          RESET PASSWORD DIALOG
      ════════════════════════════════════ */}
      <Dialog
        open={!!resetUser}
        onClose={() => setResetUser(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: "#4a148c", color: "#fff", fontWeight: 700, py: 2.5 }}>
          Reset Password — {resetUser?.name || resetUser?.email}
        </DialogTitle>
        <DialogContent sx={{ pt: "24px !important" }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1.5, color: "#333" }}>
              Choose reset method
            </FormLabel>
            <RadioGroup value={resetMode} onChange={(e) => setResetMode(e.target.value as "link" | "sms" | "manual")}>
              <Box
                sx={{
                  border: `2px solid ${resetMode === "link" ? "#7b1fa2" : "#e0e0e0"}`,
                  borderRadius: 2, p: 1.5, mb: 1.5,
                  cursor: "pointer", bgcolor: resetMode === "link" ? "#f3e5f5" : "#fafafa",
                }}
                onClick={() => setResetMode("link")}
              >
                <FormControlLabel
                  value="link"
                  control={<Radio size="small" sx={{ color: "#7b1fa2", "&.Mui-checked": { color: "#7b1fa2" } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>Send reset link via email</Typography>
                      <Typography variant="caption" color="text.secondary">
                        A secure link will be emailed to <strong>{resetUser?.email || "the user"}</strong>. Expires in 24 hours.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Box
                sx={{
                  border: `2px solid ${resetMode === "sms" ? "#7b1fa2" : "#e0e0e0"}`,
                  borderRadius: 2, p: 1.5, mb: 1.5,
                  cursor: "pointer", bgcolor: resetMode === "sms" ? "#f3e5f5" : "#fafafa",
                  opacity: resetUser?.phone ? 1 : 0.55,
                }}
                onClick={() => resetUser?.phone && setResetMode("sms")}
              >
                <FormControlLabel
                  value="sms"
                  disabled={!resetUser?.phone}
                  control={<Radio size="small" sx={{ color: "#7b1fa2", "&.Mui-checked": { color: "#7b1fa2" } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>Send reset link via SMS</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {resetUser?.phone
                          ? <>A secure link will be texted to <strong>{resetUser.phone}</strong>. Expires in 24 hours.</>
                          : "No phone number on file for this user."}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Box
                sx={{
                  border: `2px solid ${resetMode === "manual" ? "#7b1fa2" : "#e0e0e0"}`,
                  borderRadius: 2, p: 1.5,
                  cursor: "pointer", bgcolor: resetMode === "manual" ? "#f3e5f5" : "#fafafa",
                }}
                onClick={() => setResetMode("manual")}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio size="small" sx={{ color: "#7b1fa2", "&.Mui-checked": { color: "#7b1fa2" } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>Set password manually</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enter a new password directly. User will be logged out.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          </FormControl>

          {resetMode === "manual" && (
            <Box mt={2.5}>
              {/* Decoy username + password pair — Chrome / Edge run their
                  autofill scanner the moment they detect a password
                  input appear in the DOM. We give it visible-but-offscreen
                  decoys here (display:none gets skipped by some browsers)
                  so it pairs THESE inputs, not the page's search box.
                  The decoys are tabIndex=-1 + aria-hidden so keyboard
                  users + screen readers don't see them. */}
              <Box sx={{ position: "absolute", top: "-9999px", left: "-9999px", height: 0, overflow: "hidden" }}>
                <input
                  type="text"
                  name="fake-username-reset"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="username"
                  readOnly
                />
                <input
                  type="password"
                  name="fake-password-reset"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="current-password"
                  readOnly
                />
              </Box>
              <TextField
                label="New Password"
                type={showNewPw ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                required
                inputProps={{
                  // autoComplete=new-password is the standard signal:
                  // "this is a NEW password being created — do not
                  // autofill saved credentials, do not search the page
                  // for a username partner". Pair this with a unique
                  // `name` so password managers (LastPass / 1Password)
                  // don't try to match it to a saved entry. The data-*
                  // hints are vendor-specific opt-outs.
                  autoComplete: "new-password",
                  name: "user-reset-new-password",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPw(!showNewPw)} edge="end" size="small">
                        {showNewPw ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {newPassword && (
                <Box mt={0.75}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.4}>
                    <Typography variant="caption" sx={{ color: newPwStrength.color, fontWeight: 600 }}>
                      {newPwStrength.label}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(newPwStrength.score / 5) * 100}
                    sx={{
                      height: 5, borderRadius: 3,
                      bgcolor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": { bgcolor: newPwStrength.color, borderRadius: 3 },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResetUser(null)} sx={{ color: "#666" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={resetSaving || (resetMode === "manual" && newPassword.length < 8)}
            sx={{ bgcolor: "#4a148c", borderRadius: 2, px: 3, "&:hover": { bgcolor: "#6a1b9a" } }}
          >
            {resetSaving
              ? "Processing…"
              : resetMode === "link"
                ? "Send Reset Link"
                : resetMode === "sms"
                  ? "Send Reset SMS"
                  : "Set Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.name || deleteConfirm?.email}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: "#666" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Manual Reset URL (mail not configured) ── */}
      <Dialog
        open={!!manualResetUrl}
        onClose={() => { setManualResetUrl(null); setUrlCopied(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: "#f59e0b", color: "#fff", fontWeight: 700, py: 2 }}>
          ⚠️ Email Not Configured — Share Link Manually
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The mail server is not configured on this server. Copy the reset link below and send it to the user directly.
            The link expires in <strong>24 hours</strong>.
          </Typography>
          <Box
            sx={{
              bgcolor: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 2,
              p: 1.5, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all",
              color: "#1a3353",
            }}
          >
            {manualResetUrl}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setManualResetUrl(null); setUrlCopied(false); }} sx={{ color: "#666" }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleCopyResetUrl}
            sx={{
              bgcolor: urlCopied ? "#10b981" : "#1a3353",
              "&:hover": { bgcolor: urlCopied ? "#059669" : "#2d5f8a" },
              fontWeight: 700, borderRadius: 2, minWidth: 140,
            }}
          >
            {urlCopied ? "✓ Copied!" : "Copy Link"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════
          SEND CREDENTIALS DIALOG (post-creation)
      ════════════════════════════════════ */}
      <Dialog
        open={!!createdUserCredentials}
        onClose={() => setCreatedUserCredentials(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: "#1a3353", color: "#fff", fontWeight: 700, py: 2.5 }}>
          User Created Successfully
        </DialogTitle>
        <DialogContent sx={{ pt: "24px !important" }}>
          <Stack gap={2.5}>
            <Box sx={{ bgcolor: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 2, p: 2 }}>
              <Typography variant="body2" fontWeight={600} color="#2e7d32" mb={0.5}>
                Account ready
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{createdUserCredentials?.user.name || createdUserCredentials?.user.email}</strong> can now log in.
                Would you like to send their credentials via email or SMS?
              </Typography>
            </Box>

            <Stack direction="row" gap={2}>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8 }}>
                  Login Email
                </Typography>
                <Typography variant="body1" fontWeight={600} mt={0.25} sx={{ wordBreak: "break-all" }}>
                  {createdUserCredentials?.user.email || <Box component="span" sx={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 500 }}>Not provided</Box>}
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8 }}>
                  Phone Number
                </Typography>
                <Typography variant="body1" fontWeight={600} mt={0.25}>
                  {createdUserCredentials?.user.phone || <Box component="span" sx={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 500 }}>Not provided</Box>}
                </Typography>
              </Box>
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8 }}>
                Generated Password
              </Typography>
              <Stack direction="row" alignItems="center" gap={1} mt={0.5}>
                <Box
                  sx={{
                    flex: 1, bgcolor: "#f8f9fa", border: "1px solid #e0e0e0",
                    borderRadius: 2, px: 2, py: 1.25,
                    fontFamily: "monospace", fontSize: 15, fontWeight: 600,
                    letterSpacing: 1, color: "#1a3353",
                  }}
                >
                  {createdUserCredentials?.password}
                </Box>
                <Tooltip title={credsCopied ? "Copied!" : "Copy password"}>
                  <IconButton
                    onClick={handleCopyPassword}
                    sx={{ color: credsCopied ? "#10b981" : "#1a3353", border: "1px solid #e0e0e0", borderRadius: 2 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Save this password — it will not be shown again.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => { setCreatedUserCredentials(null); toast("User created successfully."); }}
            sx={{ color: "#666" }}
          >
            Skip
          </Button>
          <Tooltip title={createdUserCredentials?.user.phone ? "" : "No phone number on record"}>
            <span>
              <Button
                variant="outlined"
                startIcon={<SmsOutlinedIcon />}
                onClick={handleSendCredentialsSms}
                disabled={sendingCredsSms || sendingCreds || !createdUserCredentials?.user.phone}
                sx={{
                  borderColor: "#7b1fa2", color: "#7b1fa2",
                  borderRadius: 2, px: 3, fontWeight: 700,
                  "&:hover": { borderColor: "#4a148c", bgcolor: "rgba(123,31,162,0.04)" },
                }}
              >
                {sendingCredsSms ? "Sending…" : "Send via SMS"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={createdUserCredentials?.user.email ? "" : "No email address on record"}>
            <span>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendCredentials}
                disabled={sendingCreds || sendingCredsSms || !createdUserCredentials?.user.email}
                sx={{ bgcolor: "#2e7d32", borderRadius: 2, px: 3, "&:hover": { bgcolor: "#1b5e20" }, fontWeight: 700 }}
              >
                {sendingCreds ? "Sending…" : "Send via Email"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* ── Avatar Photo Editor (camera + crop) ───────────────────
          Modal flow: choose source (webcam / file) → crop to square
          (circular preview) → confirmed Blob flows through the same
          /utility/upload endpoint as the legacy direct-file path. */}
      <AvatarPhotoEditor
        open={photoEditorOpen}
        onClose={() => setPhotoEditorOpen(false)}
        onConfirm={handleCroppedPhoto}
      />

      {/* ── Toast ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;
